import { useCallback, useMemo } from 'react';

import { useStoreContext } from 'features/store';

import { useLoyaltyServiceRewardsQuery } from 'generated/graphql-gateway';
import { IncentiveEvaluationMap } from 'state/loyalty/hooks/types';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { useRefetchOnLoyaltyUserChange } from 'state/loyalty/hooks/use-refetch-on-loyalty-user-change';
import { useServiceModeLive } from 'state/service-mode';

import { mapServiceRewardCategories } from '../utils';
import { getLoyaltyServiceRewardServiceMode } from '../utils/service-mode';

export function useLoyaltyServiceRewards({
  rewardIds,
  skip = false,
}: { rewardIds?: string[]; skip?: boolean } = {}) {
  const serviceMode = useServiceModeLive();
  const { store } = useStoreContext();
  const storeId = store?.number;
  const { loyaltyUserId, loading: loyaltyUserIdLoading } = useLoyaltyUserId();

  const loyaltyServiceMode = getLoyaltyServiceRewardServiceMode(serviceMode);
  const querySkip = skip || loyaltyUserIdLoading;
  const variables = loyaltyUserId
    ? {
        serviceMode: loyaltyServiceMode,
        storeId,
        omitInvalids: false,
        ...(rewardIds?.length ? { rewardIds } : {}),
      }
    : { storeId, omitInvalids: false, ...(rewardIds?.length ? { rewardIds } : {}) };

  const {
    data: loyaltyServiceRewardsData,
    error: loyaltyServiceRewardsError,
    loading: loyaltyServiceRewardsLoading,
    refetch: refetchLoyaltyServiceRewards,
  } = useLoyaltyServiceRewardsQuery({
    variables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    skip: querySkip,
  });

  const {
    dataForCurrentIdentity: currentIdentityRewardsData,
    identityTransitionLoading,
    refetchForCurrentIdentity,
  } = useRefetchOnLoyaltyUserChange({
    loyaltyUserId,
    queryData: loyaltyServiceRewardsData,
    queryError: loyaltyServiceRewardsError,
    queryKey: JSON.stringify(variables),
    queryLoading: loyaltyServiceRewardsLoading,
    refetch: refetchLoyaltyServiceRewards,
    skip: querySkip,
  });

  const loading = loyaltyUserIdLoading || loyaltyServiceRewardsLoading || identityTransitionLoading;
  const error = identityTransitionLoading ? undefined : loyaltyServiceRewardsError;

  const categories = useMemo(
    () => currentIdentityRewardsData?.loyaltyServiceRewards?.categories ?? [],
    [currentIdentityRewardsData]
  );

  const { rewardCategories, sanityRewardsMap, engineRewardsMap } = useMemo(() => {
    return mapServiceRewardCategories(categories);
  }, [categories]);

  /**
   * Derive a classic `IncentiveEvaluationMap` keyed by `loyaltyEngineId`
   * (the service reward `id`) from the per-reward `errors` the service
   * already returns. This keeps the legacy `LoyaltyRewardsList` happy
   * without making a second evaluation round-trip.
   */
  const rewardsEvaluationMap = useMemo<IncentiveEvaluationMap>(() => {
    const map: IncentiveEvaluationMap = {};
    for (const category of categories) {
      for (const reward of category.rewards) {
        if (reward.errors?.length) {
          map[reward.id] = reward.errors.map(
            ({ code, currentValue, message, ruleId, targetValue }) => ({
              code,
              currentValue,
              message,
              ruleId,
              targetValue,
            })
          );
        }
      }
    }
    return map;
  }, [categories]);

  const refreshRewards = useCallback(async () => {
    if (skip || loyaltyUserIdLoading) {
      return;
    }
    await refetchForCurrentIdentity();
  }, [loyaltyUserIdLoading, refetchForCurrentIdentity, skip]);

  return {
    rewardCategories,
    sanityRewardsMap,
    engineRewardsMap,
    rewardsEvaluationMap,
    error,
    loading,
    refreshRewards,
  };
}
